import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Alert, Box, Link, Stack, Typography } from "@mui/material";
import { IconExternalLink, IconMapPin } from "@tabler/icons";
import ResourceList from "src/components/shared/ResourceList";
import { visuallyHidden } from "@mui/utils";
import ActionButton from "src/components/shared/ActionButton";
import { contentApi, rows } from "src/pages/website-content/api";
import { PostFunction } from "src/utils/axios_connection";
import UrlApi from "src/utils/Url";
import { contactInUse, isOn, matches, rights, validEmail } from "src/pages/website-content/checks";
import {
  ConfirmDialog,
  ContentPage,
  EditorDialog,
  Field,
  SaveSwitch,
  SiteBanner,
  StatusChip,
  WhereLink,
  useConfirm,
  useContentList,
  useForm,
} from "src/pages/website-content/kit";
import { ContactPreview, SitePreview } from "src/pages/website-content/Preview";

// Safrati's contact details. The website shows ONE record (contactInUse):
// the active one marked main, or else the first active one. All its emails
// and phones and its map show on the Contact page; the first ones in the
// footer and the WhatsApp/call buttons.

const EMPTY = {
  id: null,
  email: "",
  email2: "",
  email3: "",
  phone_number: "",
  phone_number2: "",
  phone_number3: "",
  location: "",
  map: "",
};
const OPTIONAL = ["email2", "email3", "phone_number2", "phone_number3"];

// "+961 76 007 996": a + and country code, then 8 to 15 digits in all.
const internationalPhone = (value) => {
  const text = String(value || "").trim();
  const digits = text.replace(/\D/g, "").length;
  return /^\+[1-9]\d{0,3}[\d\s().-]*$/.test(text) && digits >= 8 && digits <= 15;
};
// A place name: a few characters, with letters.
const placeLike = (value) => {
  const text = String(value || "").trim();
  return text.length >= 4 && /[a-z\u00C0-\u024F\u0600-\u06FF]{2}/i.test(text);
};

// Only Google Maps embeds are drawn, and only from their address: the stored
// HTML itself is never put on this page.
const MAP_SRC = /^https:\/\/www\.google\.com\/maps\/embed\?[^"'<>\s]+$/;
function mapSrc(map) {
  const text = String(map || "").trim();
  if (MAP_SRC.test(text)) return text;
  const m = /src=["']([^"']+)["']/i.exec(text);
  const src = m ? m[1].replace(/&amp;/g, "&") : "";
  return MAP_SRC.test(src) ? src : "";
}
const embedCode = (src) =>
  `<iframe src="${src}" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`;

// The map: a static card with the address and a Google Maps link, so there
// is never an empty box when the embed can't load. The embed itself loads
// only when someone asks for it.
function MapPreview({ map, title, location, startOpen = false }) {
  const src = mapSrc(map);
  const [open, setOpen] = useState(startOpen);
  const frame = { width: { xs: "100%", sm: 280 }, height: 168, borderRadius: "14px", border: 1, borderColor: "divider" };
  if (open && src) {
    return (
      <Box sx={{ width: frame.width }}>
        <Box
          component="iframe"
          src={src}
          title={title}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          sx={{ ...frame, display: "block", width: "100%" }}
        />
        <ActionButton variant="text" size="small" onClick={() => setOpen(false)} sx={{ mt: 0.5 }}>
          Hide the map
        </ActionButton>
      </Box>
    );
  }
  return (
    <Stack
      spacing={1}
      justifyContent="center"
      sx={{ ...frame, p: 2, bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#F7FAFC") }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ color: "primary.main", display: "flex", mt: "2px" }}>
          <IconMapPin size={18} aria-hidden />
        </Box>
        <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
          {location || "No address"}
        </Typography>
      </Stack>
      {location && (
        <Link
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600, minHeight: 32 }}
        >
          Open in Google Maps
          <IconExternalLink size={14} aria-hidden />
          <Box component="span" sx={visuallyHidden}>
            (opens in a new tab)
          </Box>
        </Link>
      )}
      {src ? (
        <ActionButton
          variant="outlined"
          size="small"
          onClick={() => setOpen(true)}
          aria-label={`Show the map of ${location || "this contact"}`}
          sx={{ alignSelf: "flex-start" }}
        >
          Show the map
        </ActionButton>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {map ? "The saved map is not a Google Maps embed." : "No map saved."}
        </Typography>
      )}
    </Stack>
  );
}

MapPreview.propTypes = { map: PropTypes.string, title: PropTypes.string.isRequired, location: PropTypes.string, startOpen: PropTypes.bool };

const ContactUs = () => {
  const dispatch = useDispatch();
  const can = rights("19");
  // The list says a failed load itself, with Try again, so no toast too.
  const load = useCallback(
    () => PostFunction({ Route: UrlApi.contact_us.get, data: {}, isMultipart: false, showalert: false, showerror: false, dispatch }).then(rows),
    [dispatch]
  );
  const list = useContentList(load);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const form = useForm(EMPTY);
  const confirm = useConfirm();

  const items = useMemo(
    () => [...(list.items || [])].sort((a, b) => Number(b.is_main) - Number(a.is_main) || Number(b.is_active) - Number(a.is_active) || a.id - b.id),
    [list.items]
  );
  const shown = items.filter((i) => matches(i, search, ["email", "email2", "email3", "phone_number", "phone_number2", "phone_number3", "location"]));
  // The one contact the website shows (contactInUse mirrors its rule).
  const inUse = contactInUse(items);

  const set = async (item, field, value, message) => {
    try {
      await contentApi.contact.set(item.id, field, value, dispatch);
      toast.success(message);
      // Making one record main changes the others too.
      if (field === "is_main") list.reload();
      else list.patch(item.id, { [field]: value });
    } catch {
      /* PostFunction said what went wrong */
    }
  };

  const openEditor = (item) => {
    form.reset(item ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, item[k] ?? (k === "id" ? null : "")])) : EMPTY);
    setEditing(item ? { item } : { item: null });
  };

  const save = async () => {
    const v = form.values;
    const errors = {};
    if (!validEmail(v.email)) errors.email = "Write an email address like info@safrati.ai.";
    ["email2", "email3"].forEach((k) => {
      if (v[k].trim() && !validEmail(v[k])) errors[k] = "This email address is not complete.";
    });
    ["phone_number", "phone_number2", "phone_number3"].forEach((k) => {
      if ((k === "phone_number" || v[k].trim()) && !internationalPhone(v[k])) errors[k] = "Start with + and the country code, like +961 76 007 996.";
    });
    if (!placeLike(v.location)) errors.location = "Write the town and country, like Byblos, Lebanon.";
    if (!mapSrc(v.map)) errors.map = "Paste the embed code from Google Maps: Share, then Embed a map, then Copy HTML.";
    form.setErrors(errors);
    if (Object.keys(errors).length) return false;
    const src = mapSrc(v.map);
    const body = {
      email: v.email.trim(),
      email2: v.email2.trim(),
      email3: v.email3.trim(),
      phone_number: v.phone_number.trim(),
      phone_number2: v.phone_number2.trim(),
      phone_number3: v.phone_number3.trim(),
      location: v.location.trim(),
      // Keep the code Google gave; a bare address becomes the same code.
      map: v.map.trim().startsWith("<iframe") ? v.map.trim() : embedCode(src),
    };
    try {
      if (editing.item) {
        await contentApi.contact.edit({ id: v.id, ...body }, dispatch);
        toast.success(editing.item.id === inUse?.id ? "Contact details saved. The website shows them now." : "Contact details saved.");
      } else {
        await contentApi.contact.add(body, dispatch);
        toast.success("Contact details added.");
      }
      setEditing(null);
      list.reload();
      return true;
    } catch {
      return undefined;
    }
  };

  const clearedOptional = editing?.item ? OPTIONAL.filter((k) => editing.item[k] && !form.values[k].trim()) : [];

  return (
    <ContentPage
      title="Contact details"
      intro="Safrati's email, phone, address and map, as visitors see them."
      where={
        inUse ? (
          <>
            The website shows <strong>{inUse.location || inUse.email}</strong>: all its emails and phones and its map on the{" "}
            <WhereLink page="contact" />, its first email, phone and address in the footer, and its phone in the WhatsApp and call buttons.
          </>
        ) : null
      }
      notice={
        <>
          <SiteBanner title="No inbox here yet">
            Messages sent from the website’s contact form are emailed to info@safrati.ai. They are not saved in the CMS.
          </SiteBanner>
          {list.items && !inUse && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No contact is active, so the website shows no email, phone or address. Switch one on and make it the main contact.
            </Alert>
          )}
          {inUse && !isOn(inUse.is_main) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No active contact is marked as main, so the website uses the first active one, {inUse.location || `#${inUse.id}`}. Make one the main
              contact to choose.
            </Alert>
          )}
        </>
      }
      canAdd={can.write}
      addLabel="Add contact details"
      onAdd={() => openEditor(null)}
      dialogs={
        <>
          <EditorDialog
            open={!!editing}
            title={editing?.item ? "Edit contact details" : "Add contact details"}
            subtitle={
              editing?.item
                ? editing.item.id === inUse?.id
                  ? "The website shows this contact: changes show as soon as you save."
                  : "The website doesn't show this contact; make it the main contact to show it."
                : inUse
                ? "New contacts aren't shown on the website until you make one the main contact."
                : "No other contact is active, so the website will show this one."
            }
            onClose={() => setEditing(null)}
            onSave={save}
            saveLabel={editing?.item ? "Save contact details" : "Add contact details"}
            errors={form.errors}
            dirty={form.dirty}
          >
            <Field
              label="Email"
              type="email"
              value={form.values.email}
              onChange={(v) => form.set("email", v)}
              error={form.errors.email}
              hint="Shown in the footer and on the Contact page."
            />
            <Field
              label="Phone"
              type="tel"
              value={form.values.phone_number}
              onChange={(v) => form.set("phone_number", v)}
              error={form.errors.phone_number}
              hint="Start with + and the country code, like +961 76 007 996. Also used by the WhatsApp and call buttons."
            />
            <Field
              label="Address"
              value={form.values.location}
              onChange={(v) => form.set("location", v)}
              error={form.errors.location}
              hint="Town and country. Visitors can open it in Google Maps."
            />
            <Field
              label="Map"
              value={form.values.map}
              onChange={(v) => form.set("map", v)}
              error={form.errors.map}
              hint="In Google Maps: Share, then Embed a map, then Copy HTML, and paste it here."
              multiline
              rows={3}
              sx={{ "& textarea": { fontFamily: "monospace", fontSize: "0.8125rem" } }}
            />
            {mapSrc(form.values.map) && <MapPreview map={form.values.map} title="Preview of the map" location={form.values.location} />}
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
              More emails and phones
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
              Shown on the Contact page with the first email and phone, for the contact the website uses.
            </Typography>
            <Field
              label="Second email"
              optional
              type="email"
              value={form.values.email2}
              onChange={(v) => form.set("email2", v)}
              error={form.errors.email2}
            />
            <Field
              label="Third email"
              optional
              type="email"
              value={form.values.email3}
              onChange={(v) => form.set("email3", v)}
              error={form.errors.email3}
            />
            <Field
              label="Second phone"
              optional
              type="tel"
              value={form.values.phone_number2}
              onChange={(v) => form.set("phone_number2", v)}
              error={form.errors.phone_number2}
            />
            <Field
              label="Third phone"
              optional
              type="tel"
              value={form.values.phone_number3}
              onChange={(v) => form.set("phone_number3", v)}
              error={form.errors.phone_number3}
            />
            {clearedOptional.length > 0 && (
              <Alert severity="warning">Emptying a field doesn’t remove it yet: the old value stays saved. Write the new value instead.</Alert>
            )}
            <SitePreview where="The website’s footer">
              <ContactPreview email={form.values.email.trim()} phone={form.values.phone_number.trim()} location={form.values.location.trim()} />
            </SitePreview>
          </EditorDialog>

          <ConfirmDialog
            open={confirm.open}
            title={`Delete the contact details for ${confirm.item?.location || confirm.item?.email || "this contact"}?`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onClose={confirm.close}
            onConfirm={async () => {
              try {
                await contentApi.contact.remove(confirm.item.id, dispatch);
                toast.success("Contact details deleted.");
                list.reload();
                return true;
              } catch {
                return false;
              }
            }}
          >
            {confirm.item && confirm.item.id === inUse?.id
              ? "The website shows this contact: it will show another active one, or none, until you make one the main contact."
              : "The website doesn’t show it."}{" "}
            You can restore it from the Activity log.
          </ConfirmDialog>
        </>
      }
    >
      {/* Below 600px each card's facts stack in one column, each full width,
          so the map card and the chips are never squeezed into half a card. */}
      <Box sx={{ "& li dl": { gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(auto-fill, minmax(160px, 1fr))" } } }}>
        <ResourceList
          label="Contacts"
          items={shown}
          primaryLabel="Contact"
          primary={(item) => ({
            title: item.location || "No address",
            subtitle: [item.email, item.phone_number].filter(Boolean).join(" · ") || "No email or phone",
          })}
          columns={[
            {
              key: "site",
              label: "Shown on the website",
              render: (item) =>
                item.id === inUse?.id ? (
                  <StatusChip live liveText={isOn(item.is_main) ? "Shown: main contact" : "Shown: first active"} />
                ) : (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {isOn(item.is_active) ? "Not shown: only the main contact is" : "Not shown: switched off"}
                  </Typography>
                ),
            },
            {
              key: "active",
              label: "Switched on",
              width: 170,
              render: (item) => (
                <SaveSwitch
                  label={isOn(item.is_active) ? "Switched on" : "Switched off"}
                  item={item.location || item.email}
                  checked={isOn(item.is_active)}
                  disabled={!can.edit}
                  onChange={(next) => set(item, "is_active", next ? 1 : 0, next ? "Contact switched on." : "Contact switched off.")}
                />
              ),
            },
            {
              key: "more",
              label: "More emails and phones",
              hideBelow: "lg",
              render: (item) => {
                const n = [item.email2, item.email3, item.phone_number2, item.phone_number3].filter(Boolean).length;
                return n ? `${n} more` : "—";
              },
            },
            {
              key: "map",
              label: "Map",
              render: (item) => <MapPreview map={item.map} title={`Map of ${item.location || "this contact"}`} location={item.location} />,
            },
          ]}
          onOpen={can.edit ? openEditor : undefined}
          openLabel={(item) => `Edit ${item.location || "this contact"}`}
          rowActions={(item) => [
            can.edit && { label: "Edit", onClick: () => openEditor(item) },
            can.edit &&
              !isOn(item.is_main) && { label: "Make it the main contact", onClick: () => set(item, "is_main", 1, "This is now the main contact.") },
            can.delete && { label: "Delete", destructive: true, onClick: () => confirm.ask(item) },
          ]}
          search={items.length > 3 ? { value: search, onChange: setSearch, placeholder: "Search emails, phones and places" } : undefined}
          loading={list.loading}
          error={list.failed}
          onRetry={list.reload}
          empty={{ title: "No contact details.", hint: "The website footer has no email or phone until you add them." }}
        />
      </Box>
    </ContentPage>
  );
};

export default ContactUs;
